<template>
  <div class="container-fluid">
      <div class="title-container">
        <h3 class="title">
          Finance reports - profit and loss historical
        </h3>
        <div class="ml-3 table-control d-flex align-items-center">
          <b-form-checkbox
            size="sm"
            switch
            v-model="showLastMonth"
            class="switch"
          >Show last month</b-form-checkbox>
        </div>
      </div>

    <div ref="tableContain" class="bg-white round">
      <Preloader v-if="isDataPageLoading" />
      <b-table-simple
        v-if="Object.keys(reportData).length"
        class="table__theme-default slideout__table position-relative"
        id="pl-report-historical"
        :fixed="false"
        hover
        stripped
        responsive
        bordered
      >
        <b-thead head-variant="light">
          <b-tr class="thead-row row-top" style="padding: 0px; height: 27px;" :class="{'sticky-head': getCoordinates > 0}" :style="{'top': getCoordinates + 'px'}">
            <b-th :style="{'width': thsWidth[`th_date`] + 'px'}" sticky-column>date</b-th>
            <b-th :style="{'width': thsWidth[`th_revenue`] + 'px'}" sticky-column>revenue</b-th>
            <b-th :style="{'width': thsWidth[`th_payouts`] + 'px'}" sticky-column>payouts</b-th>
            <b-th :style="{'width': thsWidth[`th_expenses`] + 'px'}" sticky-column>expenses</b-th>
            <b-th :style="{'width': thsWidth[`th_profit`] + 'px'}" sticky-column>profit</b-th>
            <b-th :style="{'width': thsWidth[`th_profit_margin`] + 'px'}" sticky-column>profit margin</b-th>
            <b-th :style="{'width': thsWidth[`th_monthly_profit`] + 'px'}" sticky-column>monthly profit</b-th>
            <b-th :style="{'width': thsWidth[`th_monthly_revenue`] + 'px'}" sticky-column>monthly revenue</b-th>
          </b-tr>
          <b-tr>
            <b-th sticky-column><div :ref="`th_date`">date</div></b-th>
            <b-th sticky-column><div :ref="`th_revenue`">revenue</div></b-th>
            <b-th class="border-right-0" sticky-column><div :ref="`th_payouts`">payouts</div></b-th>
            <b-th class="border-left-0" sticky-column><div :ref="`th_expenses`">expenses</div></b-th>
            <b-th class="border-right-0" sticky-column><div :ref="`th_profit`">profit</div></b-th>
            <b-th class="border-left-0" sticky-column><div :ref="`th_profit_margin`">profit margin</div></b-th>
            <b-th sticky-column><div :ref="`th_monthly_profit`">monthly profit</div></b-th>
            <b-th sticky-column><div :ref="`th_monthly_revenue`">monthly revenue</div></b-th>
          </b-tr>
        </b-thead>
        <b-tbody>
          <b-tr class="table-row all-time-row">
            <b-td>all time</b-td>
            <b-td>{{ reportData.summary.revenue }}</b-td>
            <b-td class="border-right-0">{{ reportData.summary.payouts }}</b-td>
            <b-td class="border-left-0">{{ reportData.summary.expenses }}</b-td>
            <b-td class="border-right-0">{{ reportData.summary.profit }}</b-td>
            <b-td class="border-left-0">
              <span :class="{'table-danger': reportData.summary.profit_margin <= 0, 'table-success': reportData.summary.profit_margin > 0}"
                    class="profit-margin-cell">{{ reportData.summary.profit_margin }}%</span>
            </b-td>
            <b-td>{{ reportData.summary.monthly_profit }}</b-td>
            <b-td>{{ reportData.summary.monthly_revenue }}</b-td>
          </b-tr>
          <b-tr class="table-row" v-for="yearData in reportData.data" :key="yearData.year">
            <b-td class="font-weight-bold">{{ yearData.year }}</b-td>
            <b-td>{{ yearData.summary.revenue }}</b-td>
            <b-td class="border-right-0">{{ yearData.summary.payouts }}</b-td>
            <b-td class="border-left-0">{{ yearData.summary.expenses }}</b-td>
            <b-td class="border-right-0">{{ yearData.summary.profit }}</b-td>
            <b-td class="border-left-0">
              <span :class="{'table-danger': yearData.summary.profit_margin <= 0, 'table-success': yearData.summary.profit_margin > 0}"
                    class="profit-margin-cell">{{ yearData.summary.profit_margin }}%</span>
            </b-td>
            <b-td>{{ yearData.summary.monthly_profit }}</b-td>
            <b-td>{{ yearData.summary.monthly_revenue }}</b-td>
          </b-tr>
          <template v-for="yearData in reportData.data">
            <b-tr class="month-row" v-for="monthData in yearData.data" :key="monthLabel(monthData.month, yearData.year)">
              <b-td>{{ monthLabel(monthData.month, yearData.year) }}</b-td>
              <b-td>{{ monthData.data.revenue }}</b-td>
              <b-td class="border-right-0">{{ monthData.data.payouts }}</b-td>
              <b-td class="border-left-0">{{ monthData.data.expenses }}</b-td>
              <b-td class="border-right-0">{{ monthData.data.profit }}</b-td>
              <b-td class="border-left-0">
                <span :class="{'table-danger': monthData.data.profit_margin <= 0, 'table-success': monthData.data.profit_margin > 0}"
                      class="profit-margin-cell">{{ monthData.data.profit_margin }}%</span>
              </b-td>
            </b-tr>
            <b-tr class="table-row" :key="yearData.year + '-months-divider'">
              <b-td style="padding:0px" class="border-top-0" colspan="6"></b-td>
            </b-tr>
          </template>
        </b-tbody>
      </b-table-simple>
    </div>
    <div class="mt-4">
      <h3 class="title">Charts</h3>
    </div>
    <div class="bg-white round p-2">
      <div class="row">
        <div class="col-12 pb-4">
          <canvas id="barChart">Your browser does not support the canvas element.</canvas>
        </div>
      </div>
      <div class="row">
        <div class="col-6">
          <canvas id="profitMarginChart">Your browser does not support the canvas element.</canvas>
        </div>
        <div class="col-6">
          <canvas id="revenueProfitChart">Your browser does not support the canvas element.</canvas>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { permissions } from '@mocks/permissions'
import StickyHeaderMixin from '../mixins/StickyHeaderMixin'
import Chart from 'chart.js'
import Preloader from '@components/Preloader'

export default {
  name: 'FinanceReportsProfitAndLossHistorical',
  mixins: [ StickyHeaderMixin ],
  components: {
    Preloader
  },
  data () {
    return {
      permissions: permissions,
      isDataPageLoading: false,
      showLastMonth: false,
      reportData: {
        'data': [],
        'summary': {}
      },
      barChart: null,
      profitMarginChart: null,
      revenueProfitChart: null
    }
  },
  computed: {
    getDataByMonths () {
      const monthData = []
      const reportData = this.reportData.data
      for (const yi in reportData) {
        const year = reportData[yi].year
        const yearData = reportData[yi].data
        for (const mi in yearData) {
          const data = yearData[mi].data
          monthData.push({ month: yearData[mi].month, year, data })
        }
      }
      return monthData.reverse()
    }
  },
  watch: {
    async showLastMonth (value) {
      await this.fetchReportData(value)
      this.barChart.destroy()
      this.profitMarginChart.destroy()
      this.revenueProfitChart.destroy()
      this.createCharts()
    }
  },
  async created () {
    await this.fetchReportData(this.showLastMonth)
    this.createCharts()
  },
  methods: {
    async fetchReportData (showLastMonth = false) {
      return this._toggleDataPageLoading(async () => {
        await this.$api.finances
          .getReportsProfitAndLossHistorical(showLastMonth)
          .then((response) => {
            this.reportData = response.data
          })
      })
    },
    monthLabel (month, year) {
      return year + '-' + month
    },
    createCharts () {
      const barChartCont = document.getElementById('barChart').getContext('2d')

      const monthsLabels = this.getDataByMonths.map(monthData => this.monthLabel(monthData.month, monthData.year))

      const payouts = this.getDataByMonths.map(monthData => monthData.data['payouts'] ?? 0)
      const expenses = this.getDataByMonths.map(monthData => monthData.data['expenses'] ?? 0)
      const profit = this.getDataByMonths.map(monthData => monthData.data['profit'] ?? 0)

      this.barChart = new Chart(barChartCont, {
        type: 'bar',
        data: {
          labels: monthsLabels,
          datasets: [
            {
              label: 'Payouts',
              data: payouts,
              stack: 'month',
              backgroundColor: '#7eb0d5',
              borderColor: '#7eb0d5',
              borderWidth: 1
            },
            {
              label: 'Expenses',
              data: expenses,
              stack: 'month',
              backgroundColor: '#fd7f6f',
              borderColor: '#fd7f6f',
              borderWidth: 1
            },
            {
              label: 'Profit',
              data: profit,
              stack: 'month',
              backgroundColor: '#ffb55a',
              borderColor: '#ffb55a',
              borderWidth: 1
            }
          ]
        },
        options: {
          legend: {
            labels: {
              fontSize: 16
            }
          }
        }
      })

      const profitMarginChartCont = document.getElementById('profitMarginChart').getContext('2d')

      const profitMargin = this.getDataByMonths.map(monthData => monthData.data['profit_margin'] ?? 0)

      this.profitMarginChart = new Chart(profitMarginChartCont, {
        type: 'line',
        data: {
          labels: monthsLabels,
          datasets: [
            {
              label: 'Profit margin',
              data: profitMargin,
              fill: false,
              borderColor: '#ffb55a',
              borderWidth: 3,
              lineTension: 0
            }
          ]
        }
      })

      const revenueProfitChartCont = document.getElementById('revenueProfitChart').getContext('2d')

      const revenue = this.getDataByMonths.map(monthData => monthData.data['revenue'] ?? 0)

      this.revenueProfitChart = new Chart(revenueProfitChartCont, {
        type: 'line',
        data: {
          labels: monthsLabels,
          datasets: [
            {
              label: 'Profit',
              data: profit,
              fill: false,
              borderColor: '#ffb55a',
              borderWidth: 3,
              lineTension: 0
            },
            {
              label: 'Revenue',
              data: revenue,
              fill: false,
              borderColor: '#beb9db',
              borderWidth: 3,
              lineTension: 0
            }
          ]
        }
      })
    }
  }
}

</script>

<style lang="scss">
  #pl-report-historical {
    thead tr th {
      padding: 5px 10px;
    }
    tbody td, thead tr th {
      text-align: center;
      border: 2px solid #ababab;
      padding: 4px;
    }
    tr.month-row td {
      border-top: 1px solid #eeeeee;
      border-bottom: 1px solid #eeeeee;
    }
    tbody tr {
      border-bottom: none;
    }
    .all-time-row {
      font-weight:bold;
    }
    .profit-margin-cell {
      padding: 2px;
      min-width: 90px;
      display: inline-block;

    }
  }
</style>
